import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Separator,
  Badge,
  Button,
} from '@chakra-ui/react';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { useAuth } from '../hooks/useAuth';
import { NeurevLogo } from './NeurevLogo';
import { toaster } from './ui/toaster';
import {
  FiHome,
  FiUsers,
  FiShield,
  FiFileText,
  FiDollarSign,
  FiBarChart,
  FiSettings,
  FiActivity,
  FiLifeBuoy,
  FiBell,
  FiBookOpen,
  FiDatabase,
  FiLock,
  FiCreditCard
} from 'react-icons/fi';

interface SidebarSection {
  title: string;
  icon: React.ElementType;
  items: SidebarItem[];
}

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
  description?: string;
}

const sidebarSections: SidebarSection[] = [
  {
    title: 'admin.main.sidebar.sections.coreManagement',
    icon: FiHome,
    items: [
      { id: 'dashboard', label: 'admin.main.sidebar.items.dashboard.label', icon: FiHome, description: 'admin.main.sidebar.items.dashboard.description' },
      { id: 'users', label: 'admin.main.sidebar.items.users.label', icon: FiUsers, description: 'admin.main.sidebar.items.users.description' },
      { id: 'standards', label: 'admin.main.sidebar.items.standards.label', icon: FiShield, description: 'admin.main.sidebar.items.standards.description' },
      { id: 'content', label: 'admin.main.sidebar.items.content.label', icon: FiFileText, description: 'admin.main.sidebar.items.content.description' }
    ]
  },
  {
    title: 'admin.main.sidebar.sections.businessOperations',
    icon: FiDollarSign,
    items: [
      { id: 'billing', label: 'admin.main.sidebar.items.billing.label', icon: FiDollarSign, description: 'admin.main.sidebar.items.billing.description' },
      { id: 'subscription_plans', label: 'admin.main.sidebar.items.subscriptionPlans.label', icon: FiCreditCard, description: 'admin.main.sidebar.items.subscriptionPlans.description' },
      { id: 'analytics', label: 'admin.main.sidebar.items.analytics.label', icon: FiBarChart, description: 'admin.main.sidebar.items.analytics.description' },
      { id: 'security', label: 'admin.main.sidebar.items.security.label', icon: FiShield, description: 'admin.main.sidebar.items.security.description' }
    ]
  },
  {
    title: 'admin.main.sidebar.sections.systemAdministration',
    icon: FiSettings,
    items: [
      { id: 'system', label: 'admin.main.sidebar.items.system.label', icon: FiActivity, description: 'admin.main.sidebar.items.system.description' },
      { id: 'configuration', label: 'admin.main.sidebar.items.configuration.label', icon: FiSettings, description: 'admin.main.sidebar.items.configuration.description' },
      { id: 'backup', label: 'admin.main.sidebar.items.backup.label', icon: FiDatabase, description: 'admin.main.sidebar.items.backup.description' }
    ]
  },
  {
    title: 'admin.main.sidebar.sections.supportOperations',
    icon: FiLifeBuoy,
    items: [
      { id: 'support', label: 'admin.main.sidebar.items.support.label', icon: FiLifeBuoy, description: 'admin.main.sidebar.items.support.description' },
      { id: 'notifications', label: 'admin.main.sidebar.items.notifications.label', icon: FiBell, description: 'admin.main.sidebar.items.notifications.description' },
      { id: 'documentation', label: 'admin.main.sidebar.items.documentation.label', icon: FiBookOpen, description: 'admin.main.sidebar.items.documentation.description' }
    ]
  }
];

interface AdminSidebarProps {
  currentSection: string;
  onSectionChange: (section: string) => void;
}


const AdminSidebar: React.FC<AdminSidebarProps> = ({ currentSection, onSectionChange }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeColor = useColorModeValue('blue.600', 'blue.200');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const sectionColor = useColorModeValue('gray.500', 'gray.400');

  const handleReturnToUser = () => {
    window.location.href = '/dashboard';
  };

  const handleSignOut = () => {
    logout();
    toaster.create({
      title: t('admin.main.sidebar.signedOutSuccessfully'),
      description: t('admin.main.sidebar.signedOutDescription'),
      type: 'success',
    });
  };

  return (
    <Box
      w={{ base: "100%", lg: "300px" }}
      bg={bg}
      borderRight={{ base: "none", lg: "1px" }}
      borderColor={borderColor}
      h={{ base: "100%", lg: "100vh" }}
      position={{ base: "relative", lg: "sticky" }}
      top={0}
      display="flex"
      flexDirection="column"
    >
      {/* Fixed Logo Header */}
      <Box p={4} pb={2} flexShrink={0}>
        <VStack gap={2} align="center" mb={1}>
          <NeurevLogo height={70} />
          <Text fontSize="md" fontWeight="bold" color={textColor}>
            {t('admin.main.adminPortal')}
          </Text>
        </VStack>
      </Box>

      {/* Scrollable Navigation */}
      <Box flex={1} overflowY="auto" px={4} pb={4}>
        <VStack gap={4} align="stretch">
          {/* Navigation Sections */}
          {sidebarSections.map((section, sectionIndex) => (
            <Box key={section.title}>
              {/* Section Header */}
              <HStack gap={2} mb={3}>
                <Icon as={section.icon} color={sectionColor} />
                <Text fontSize="sm" fontWeight="semibold" color={sectionColor} textTransform="uppercase" letterSpacing="wide">
                  {t(section.title)}
                </Text>
              </HStack>

              {/* Section Items */}
              <VStack gap={1} align="stretch">
                {section.items.map((item) => {
                  const isActive = currentSection === item.id;
                  return (
                    <Box
                      key={item.id}
                      as="button"
                      w="full"
                      textAlign="left"
                      p={3}
                      borderRadius="md"
                      bg={isActive ? activeBg : 'transparent'}
                      color={isActive ? activeColor : textColor}
                      _hover={{
                        bg: isActive ? activeBg : hoverBg,
                      }}
                      onClick={() => onSectionChange(item.id)}
                      transition="all 0.2s"
                      position="relative"
                    >
                      <HStack gap={3}>
                        <Icon as={item.icon} boxSize={4} />
                        <VStack gap={0} align="start" flex={1}>
                          <HStack gap={2} w="full">
                            <Text fontSize="sm" fontWeight={isActive ? "semibold" : "medium"}>
                              {t(item.label)}
                            </Text>
                            {item.badge && (
                              <Badge size="sm" colorScheme="blue" variant="subtle">
                                {item.badge}
                              </Badge>
                            )}
                          </HStack>
                          {item.description && (
                            <Text fontSize="xs" color={sectionColor} lineClamp={1}>
                              {t(item.description)}
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>

              {/* Section Divider */}
              {sectionIndex < sidebarSections.length - 1 && (
                <Separator my={4} borderColor={borderColor} />
              )}
            </Box>
          ))}

          {/* Admin Actions */}
          <Separator borderColor={borderColor} />
          <VStack gap={2} align="stretch">
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={handleReturnToUser}
            >
              <Icon as={FiHome} />
              {t('admin.main.sidebar.returnToUserPortal')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={handleSignOut}
            >
              <Icon as={FiLock} />
              {t('admin.main.sidebar.signOut')}
            </Button>
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default AdminSidebar;
